import * as React from 'react';
import * as moment from 'moment';
import { useMemo } from 'react';
import { useAppHookContext } from '@nkr/utils/rest-api';
import useCoachCounts from '@nkr/store/microsites/hooks/use-coach-counts';
import useCoachFilteredList, { CoachListTab, ICoachFilteredListItem, ICoachFilteredListRequest } from "@nkr/store/microsites/hooks/use-coach-filtered-list";
import useCoachList from '@nkr/store/microsites/hooks/use-coach-list';
import useSelectedCoach from '@nkr/store/microsites/hooks/use-selected-coach';
import { CellClickedEvent, ColDef, FirstDataRenderedEvent, GridApi, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Glyphicon, Label } from 'react-bootstrap';
import ClipButton from '@nkr/components/clip-button';
import OverlayMenuButton, { IMenuItem } from '@nkr/components/overlay-menu-button';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import CommStatusRenderer from './comm-status-renderer';
import CoachFilterBar, { defaultCoachFilters, ICoachFilters } from './coach-filter-bar';
import CoachTabs, { readCoachSearchFromUrl } from './coach-tabs';
import VoucherTransplantDisplay from './voucher-transplant-display';
import useCoachFilterLookupData from '@nkr/store/microsites/hooks/use-coach-filter-lookup-data';
import CoachDrawer from './coach-drawer';
import { LoadingIndicator } from '@nkr/components/loading-indicator';
import { coachPickerStyles, ContentWrapper, gridStyles, GridWrapper } from './styles';
import { CustomSiteStatus } from '@nkr/enums/microsite-recipient-status';


interface IOwnProps {
    tab: CoachListTab;
}

type TProps = IOwnProps;

const excludedClickFields = ['commStatus', 'hideFromCoach', 'shortCode', 'customSite'];
const maxCopyEmailRecords = 100;

const CoachFilteredList: React.FC<TProps> = ({ tab }) => {
    const { app } = useAppHookContext();
    const { contactId, group } = app.user;
    const { coachList } = useCoachList();
    const { activeContactId, setCoach, isSelected } = useSelectedCoach(contactId, group.isSuper);

    React.useEffect(() => {
        if (group.isSuper && coachList.length > 0 && !isSelected) {
            setCoach(coachList[0].contactId);
        }
    }, [coachList]);
    const initialFilters: ICoachFilters = { ...defaultCoachFilters, search: readCoachSearchFromUrl() };
    const [filters, setFilters] = React.useState<ICoachFilters>(initialFilters);
    const [debouncedFilters, setDebouncedFilters] = React.useState<ICoachFilters>(initialFilters);
    const [sortBy, setSortBy] = React.useState<string>(null);
    const [sortDescending, setSortDescending] = React.useState<boolean>(false);
    const [page, setPage] = React.useState<number>(1);
    const [selectedId, setSelectedId] = React.useState<number | null>(null);
    const [refreshKey, setRefreshKey] = React.useState<number>(0);
    const [copyEmailsMessage, setCopyEmailsMessage] = React.useState<{ text: string, isError: boolean } | null>(null);
    const pageSize = 100;
    const gridApi = React.useRef<GridApi>(null);
    const { counts } = useCoachCounts(activeContactId, refreshKey);
    const { coachStatusList, eventTypeList } = useCoachFilterLookupData();

    React.useEffect(() => {
        const timer = setTimeout(() => setDebouncedFilters(filters), 300);
        return () => clearTimeout(timer);
    }, [filters]);

    const request: ICoachFilteredListRequest = {
        coachContactId: activeContactId,
        tab,
        organType: debouncedFilters.organType >= 0 ? debouncedFilters.organType : undefined,
        languagePreference: debouncedFilters.languagePreference >= 0 ? debouncedFilters.languagePreference : undefined,
        messageStatus: debouncedFilters.messageStatus >= 0 ? debouncedFilters.messageStatus : undefined,
        centerId: debouncedFilters.centerId !== 0 ? debouncedFilters.centerId : undefined,
        coachStatus: debouncedFilters.coachStatus > 0 ? debouncedFilters.coachStatus : undefined,
        status: debouncedFilters.status >= 0 ? debouncedFilters.status : undefined,
        search: debouncedFilters.search || undefined,
        showHidden: debouncedFilters.showHidden || undefined,
        sortBy: sortBy || undefined,
        sortDescending: sortDescending || undefined,
        page,
        pageSize
    };

    const { loading, error, items, total, toggleHide, updateItem } = useCoachFilteredList(request, refreshKey);
    const totalPages = Math.ceil(total / pageSize) || 1;
    const toggleHideRef = React.useRef(toggleHide);
    toggleHideRef.current = toggleHide;

    React.useEffect(() => {
        if (!loading) {
            gridApi.current?.sizeColumnsToFit();
        }
    }, [loading]);

    const columns: ColDef[] = [
        {
            colId: 'commStatus', field: 'commStatus', width: 80, suppressAutoSize: true, comparator: () => 0, sortingOrder: ['asc'],
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p}>Msg<br />Status</SortableHeaderComponent>,
            cellRendererFramework: (p: ICellRendererParams) => (
                <a href={`/microsite/coach/${(p.data as ICoachFilteredListItem).id}`} target='_blank'>
                    <CommStatusRenderer {...p} />
                </a>
            ),
        },
        {
            field: 'hideFromCoach', width: 45, suppressAutoSize: true, sortable: false,
            headerComponentFramework: () => <div>Hide</div>,
            cellRendererFramework: (p: ICellRendererParams) => (
                <Glyphicon glyph={p.value ? 'check' : 'unchecked'} onClick={() => toggleHideRef.current(p.data)} />
            ),
        },
        {
            colId: 'micrositeType', field: 'micrositeType', width: 90, comparator: () => 0,
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p}>Organ<br />Type</SortableHeaderComponent>,
        },
        {
            colId: 'coachStatusDescription', field: 'coachStatusDescription', comparator: () => 0,
            hide: tab === CoachListTab.Approval,
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p}>Coach Status</SortableHeaderComponent>,
        },
        {
            colId: 'center.alias', field: 'centerAlias', width: 80, comparator: () => 0,
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p}>Center</SortableHeaderComponent>,
        },
        {
            colId: 'fullName', field: 'lastName', comparator: () => 0,
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p}>Name</SortableHeaderComponent>,
            valueGetter: ({ data }: ValueGetterParams) => `${data.firstName} ${data.lastName}`,
        },
        {
            colId: 'birthdate', field: 'birthdate', width: 120, comparator: () => 0,
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p}>Birth Date<br />(Age)</SortableHeaderComponent>,
            cellRendererFramework: ({ data }: ICellRendererParams) => <>{data.birthdate ? moment(data.birthdate).format('MM/DD/YYYY') : ''} ({data.age})</>,
        },
        {
            colId: 'languagePreference', field: 'languagePreference', width: 100, comparator: () => 0,
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p}>Lang.<br />Pref.</SortableHeaderComponent>,
        },
        {
            colId: 'starterSiteActiveDate', field: 'starterSiteActiveDate', width: 140, comparator: () => 0,
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p}>Starter Site<br />Active</SortableHeaderComponent>,
            cellRendererFramework: ({ data }: ICellRendererParams) => {
                const m = moment(data.starterSiteActiveDate);
                return <>{m.isValid() && m.isAfter('2008-01-01') ? m.format('MM/DD/YYYY') : '-'}</>;
            },
        },
        {
            colId: 'customSiteApprovalOwner', field: 'customSiteApprovalOwner', width: 140, comparator: () => 0,
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p}>Custom Site<br />Approval Owner</SortableHeaderComponent>,
            cellRendererFramework: ({ data }: ICellRendererParams) => <>{data.customSiteApprovalOwner ?? '-'}</>,
        },
        {
            colId: 'customSiteApprovalWaitDays', field: 'customSiteApprovalWaitDays', width: 140, comparator: () => 0,
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p}>Custom Site<br />Approval Wait</SortableHeaderComponent>,
            cellRendererFramework: ({ data }: ICellRendererParams) => <>{data.customSiteApprovalWaitDays != null ? `${data.customSiteApprovalWaitDays} days` : '-'}</>,
        },
        {
            colId: 'currentStatusDescription', field: 'currentStatusDescription', cellClass: 'no-wrap', width: 190, comparator: () => 0,
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p}>Status</SortableHeaderComponent>,
            cellRendererFramework: ({ value }: ICellRendererParams) => <div style={gridStyles.cellSmallText}>{value}</div>,
        },
        {
            colId: 'mostRecentCoachContactDateTime', field: 'mostRecentCoachContactDateTime', width: 160, comparator: () => 0,
            hide: tab === CoachListTab.Approval,
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p}>Most Recent<br />Contact Attempt</SortableHeaderComponent>,
            cellRendererFramework: ({ data }: ICellRendererParams) => <>{data.mostRecentCoachContactDateTime ? moment(data.mostRecentCoachContactDateTime).format('MM/DD/YYYY') : ''}</>,
        },
        {
            colId: 'nextFollowupDate', field: 'nextFollowupDate', width: 140, comparator: () => 0,
            hide: tab === CoachListTab.Approval,
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p}>Next Followup<br />Date</SortableHeaderComponent>,
            cellRendererFramework: ({ data }: ICellRendererParams) => <>{data.nextFollowupDate ? moment(data.nextFollowupDate).format('MM/DD/YYYY') : '-'}</>,
        },
        {
            colId: 'coachContact.fullName', field: 'coachFullName', width: 220, comparator: () => 0,
            hide: !group.isSuper,
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p}>Coach</SortableHeaderComponent>,
        },
        {
            colId: 'shortCode', field: 'shortCode', sortable: false, width: 130,
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p}>Microsite URL</SortableHeaderComponent>,
            cellRendererFramework: ({ data }: ICellRendererParams) => {
                const url = `${__NKR_ORG_URL__}/${data.shortCode}`;
                return (
                    <div>
                        <a href={url} title={url} target='_blank'>{'.../' + data.shortCode}</a>&nbsp;
                        <ClipButton value={url} title='Copy URL to Clipboard' />
                    </div>
                );
            },
        },
        {
            colId: 'businessCardsRequestDate', field: 'businessCardsRequestDate', width: 160, comparator: () => 0,
            hide: tab === CoachListTab.Approval,
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p}>Business Cards<br />Requested</SortableHeaderComponent>,
            cellRendererFramework: ({ data }: ICellRendererParams) => <>{data.businessCardsRequestDate ? moment(data.businessCardsRequestDate).format('MM/DD/YYYY') : ''}</>,
        },
        {
            colId: 'vtxpStatus', field: 'vtxpStatus', width: 160, comparator: () => 0,
            hide: tab === CoachListTab.Approval,
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent title='Voucher/Transplant Details' {...p}>Voucher/TXP<br />Details</SortableHeaderComponent>,
            cellRendererFramework: ({ data }: ICellRendererParams) => {
                const logs = (data as ICoachFilteredListItem).voucherOrTransplantEvents;
                return logs?.length > 0 ? <VoucherTransplantDisplay logs={logs} eventTypeList={eventTypeList} /> : <>-</>;
            },
        },
        {
            colId: 'micrositeBannerStatusDesc', field: 'micrositeBannerStatusDesc', width: 100, comparator: () => 0,
            headerComponentFramework: (p: IHeaderParams) => <SortableHeaderComponent {...p} title='Post Transplant Banner Status'>PTM<br />Banner</SortableHeaderComponent>,
            cellRendererFramework: ({ data }: ICellRendererParams) => {
                const { micrositeBannerStatusId: statusId, micrositeBannerStatusDesc: desc } = data as ICoachFilteredListItem;
                let bsStyle = '';
                let label = desc;
                if (statusId === 0) { label = '--'; }
                else if (statusId === 1) { bsStyle = 'default'; }
                else { bsStyle = 'success'; }
                return <Label bsStyle={bsStyle} style={gridStyles.cellSmallText}>{label}</Label>;
            },
        },
        {
            colId: 'customSite', field: 'customSite', sortable: false, width: 100,
            hide: tab !== CoachListTab.Approval && tab !== CoachListTab.DeclinedCs,
            headerComponentFramework: () => <div>Actions</div>,
            cellRendererFramework: ({ data }: ICellRendererParams) => {
                const item = data as ICoachFilteredListItem;
                const menu: IMenuItem[] = [];
                
                if (item.customSiteStatusId === CustomSiteStatus.PendCoachApproval) {
                    menu.push({ id: 0, action: 0, url: `/microsite/coach/approve/${item.id}`, include: true, selectable: false, element: <>Review Custom Site</> });
                    menu.push({ id: 1, action: 1, url: `/microsite/coach/decline/${item.id}`, include: true, selectable: false, element: <>Decline Custom Site</> });
                }
                
                menu.push({ id: 2, action: 2, url: `/microsite/coach/history/${item.id}?returnTo=${window.location.pathname.slice(1)}`, include: true, selectable: false, element: <>View Activity Log</> });
                
                return (
                    <OverlayMenuButton
                        menu={menu}
                        bsStyle='primary'
                        bsSize='xs'
                        title='Actions'
                        listItemStyle={{ padding: '2px 7.5px', fontSize: 14 }}
                        includeCaret={true}
                        onToggle={() => {}}
                        onSelect={() => {}}
                    />
                );
            },
        },
    ];

    const columnDefs = useMemo(() => columns, [tab]);
    const rowData = useMemo(() => items, [items]);

    const handleSortChanged = (e: SortChangedEvent) => {
        const sortModel = e.api.getSortModel();
        if (sortModel.length > 0) {
            setSortBy(sortModel[0].colId);
            setSortDescending(sortModel[0].sort === 'desc');
        } else {
            setSortBy(null);
            setSortDescending(false);
        }
        setPage(1);
    };

    const handleFilterChange = (f: ICoachFilters) => {
        setFilters(f);
        setPage(1);
    };

    const handleReset = () => {
        setFilters(defaultCoachFilters);
        setDebouncedFilters(defaultCoachFilters);
        setPage(1);
    };

    const handleCopyEmails = async () => {
        if (total > maxCopyEmailRecords) {
            setCopyEmailsMessage({ text: `Cannot copy emails for more than ${maxCopyEmailRecords} records at once (${total} match the current filters). Narrow the filters and try again.`, isError: true });
            return;
        }

        const emails = Array.from(new Set(items.map(x => x.email?.trim()).filter(x => !!x)));

        try {
            await navigator.clipboard.writeText(emails.join('; '));
            setCopyEmailsMessage({ text: `Copied ${emails.length} email${emails.length === 1 ? '' : 's'} to clipboard.`, isError: false });
        } catch (err) {
            setCopyEmailsMessage({ text: 'Failed to copy emails to clipboard.', isError: true });
        }
    };

    React.useEffect(() => {
        setCopyEmailsMessage(null);
    }, [items]);

    const handleItemUpdated = (patch: Partial<ICoachFilteredListItem>) => {
        if (selectedId) {
            updateItem(selectedId, patch);
        }
        setRefreshKey(k => k + 1);
    };

    return (
        <ContentWrapper className='container-fluid'>
            {group.isSuper && (
                <select
                    className='form-control'
                    style={coachPickerStyles.select}
                    value={activeContactId ?? ''}
                    onChange={e => setCoach(Number(e.target.value))}
                >
                    {coachList.map(c => (
                        <option key={c.id} value={c.contactId}>{c.firstName} {c.lastName}</option>
                    ))}
                </select>
            )}
            <CoachTabs contactId={activeContactId} counts={counts} search={filters.search} />
            <CoachFilterBar
                filters={filters}
                onChange={handleFilterChange}
                onReset={handleReset}
            />
            {error && <div>{error}</div>}
            <LoadingIndicator type='spin' color='black' loading={loading} />
            <div style={{ display: loading ? 'none' : '' }}>
                <GridWrapper className='ag-theme-bootstrap'>
                    <AgGridReact
                        defaultColDef={{ sortable: true }}
                        columnDefs={columnDefs}
                        rowData={rowData}
                        rowHeight={30}
                        headerHeight={54}
                        suppressCellSelection={true}
                        enableCellTextSelection={true}
                        suppressContextMenu={true}
                        noRowsOverlayComponentFramework={() => <div>No patients found.</div>}
                        onGridReady={e => { gridApi.current = e.api; }}
                        onSortChanged={handleSortChanged}
                        onCellClicked={(e: CellClickedEvent) => {
                            if (!excludedClickFields.includes(e.colDef.field)) {
                                setSelectedId((e.data as ICoachFilteredListItem).id);
                            }
                        }}
                        onFirstDataRendered={(e: FirstDataRenderedEvent) => e.api.sizeColumnsToFit()}
                        onRowDataChanged={(e: RowDataChangedEvent) => e.api.sizeColumnsToFit()}
                        onGridSizeChanged={(e: GridSizeChangedEvent) => e.api.sizeColumnsToFit()}
                    />
                </GridWrapper>
                <div style={gridStyles.paginationRow}>
                    <button className='btn btn-default btn-sm' disabled={page <= 1} onClick={() => setPage(p => p - 1)}>&#8249; Prev</button>
                    <span>Showing {total === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</span>
                    <button className='btn btn-default btn-sm' disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next &#8250;</button>
                    <button className='btn btn-primary btn-sm' disabled={total === 0} onClick={handleCopyEmails}>Copy Emails to Clipboard</button>
                    {copyEmailsMessage && <span className={copyEmailsMessage.isError ? 'text-danger' : 'text-success'}>{copyEmailsMessage.text}</span>}
                </div>
            </div>
            <CoachDrawer
                id={selectedId}
                coachStatusList={coachStatusList}
                isAdmin={group.isSuper}
                currentTab={String(tab)}
                onClose={() => setSelectedId(null)}
                onItemUpdated={handleItemUpdated}
            />
        </ContentWrapper>
    );
};

export default CoachFilteredList;
